import { CalendarDotsIcon, ClockIcon, TrashIcon } from '@phosphor-icons/react';
import { PageHeader } from 'components/PageHeader';
import { CreditNoteStatus, OrderLineStopType } from 'src/types/enums';
import { InternalRole } from '../../../auth/endpoints/MeEndpoint.types';
import React, { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { invariant, nullthrows } from '@utils/invariant';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';
import { runPdfJobAndRefresh } from '../../../../utils/pdf-job-client';
import {
  approveCreditNote,
  deleteCreditNote,
  generateCreditNote,
  refreshCreditNotePeppolStatus,
  sendCreditNoteViaPeppol,
  useCreditNote,
} from '../../models';
import { SendCreditNoteDialog } from './SendCreditNoteDialog';

import { Breadcrumb } from '../../../../components/Breadcrumb';
import { DataField } from '../../../../components/DataField';
import * as Dialog from '@radix-ui/react-dialog';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { formatDate, formatDateTime, formatInputTime } from '../../../../utils/date';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber } from '../../../../utils/number';
import { FileUploaderButton } from '../../../document/components/FileUploaderButton';
import { getInvoiceTotals } from '../../../../domain/invoice-totals';
import { InputField } from '../../../../components/input/InputField';
import { FormDialog } from '../../../../components/dialog/FormDialog';
import { useAuth } from '../../../../contexts/auth-context';
import { PageHeading } from 'components/PageHeading';
import { roleToNumber } from 'src/app/auth/roles.client';
import { Button } from '../../../../components/button/Button';
import { BaseButton } from '../../../../components/button/BaseButton';
import { DelayedSpinner } from '../../../../components/Spinner';
import { Card } from '../../../../components/Card';
import { SummaryBar } from '../../../../components/SummaryBar';
import { TotalsCard } from '../../../../components/TotalsCard';
import { PeppolStatusCard } from '../../../peppol/components/PeppolStatusCard';
import { AuditLogsList } from '../../../audit-log/components/AuditLogsList';

const CreditNotePage = () => {
  const { me } = useAuth();
  const navigate = useNavigate();
  const { creditNoteId } = useParams<{ creditNoteId: string }>();
  invariant(creditNoteId);
  const { data, isLoading, mutate } = useCreditNote(creditNoteId);

  // Bumped on every refresh so the history list reloads, also when no credit note field changed (e.g. an email re-send).
  const [historyVersion, setHistoryVersion] = useState(0);
  const refreshData = useCallback(() => {
    setHistoryVersion((v) => v + 1);
    return mutate();
  }, [mutate]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const creditNote = nullthrows(data, 'Creditnota niet gevonden');
  const totals = getInvoiceTotals([
    ...creditNote.creditNoteOrders
      .map((o) =>
        o.lines
          .map((l) => {
            return {
              ...l,
              vatRateId: l.vatRate.id,
            };
          })
          .flat(),
      )
      .flat(),
    ...creditNote.manualLines.map((v) => {
      return {
        ...v,
        vatRateId: v.vatRate.id,
      };
    }),
  ]);

  const isAdmin = roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin);
  const isDraft = creditNote.status === CreditNoteStatus.Draft;
  const title = `Creditnota ${creditNote.creditNoteNumber ?? 'DRAFT'}`;
  return (
    <>
      <PageHeader title={title} />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            parentItem={{
              name: "Creditnota's",
              to: '/internal/finance/creditnotes',
            }}
            currentItem={title}
          />
        }
        rightSide={
          <div className="flex gap-2">
            <div>
              <FileUploaderButton
                buttonText="CDN"
                title="Creditnota"
                file={creditNote.document}
                generateDocument={async () => {
                  await runPdfJobAndRefresh(await generateCreditNote(creditNote.id), async () => {
                    await refreshData();
                  });
                  trackEvent('creditnote_generated', { credit_note_id: creditNote.id });
                }}
                disableUpload
              />
            </div>

            {creditNote.status === CreditNoteStatus.Draft && (
              <FormDialog
                triggerText="Creditnota goedkeuren"
                title="Ben je zeker dat je de creditnota wilt goedkeuren?"
                submitText="Creditnota goedkeuren"
                initialValues={{ creditNoteNumber: '' }}
                onSubmit={async (values) => {
                  const creditNoteNumber = values.creditNoteNumber.trim();

                  try {
                    await approveCreditNote(creditNote.id, creditNoteNumber ? +creditNoteNumber : undefined);
                    await refreshData();
                    trackEvent('creditnote_approved', {
                      credit_note_id: creditNote.id,
                      manual_number: Boolean(creditNoteNumber),
                    });
                    toast.success('Creditnota goedgekeurd');
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon creditnota niet goedkeuren: ${getDisplayError(err)}`);
                  }
                }}
              >
                <div>Ben je zeker dat je deze creditnota wilt goedkeuren?</div>

                <div>
                  Vul het credit nota nummer alleen in als je manueel een ander factuurnummer wilt gebruiken (uit het
                  verleden), anders zal het de automatische nummering de mist in laten gaan.
                </div>

                <InputField labelText="Credit nota nummer" name="creditNoteNumber" type="number" />
              </FormDialog>
            )}

            {creditNote.status !== CreditNoteStatus.Draft && (
              <SendCreditNoteDialog
                creditNote={creditNote}
                onSent={async () => {
                  await refreshData();
                }}
              />
            )}

            {isAdmin && isDraft && (
              <ConfirmDialog
                triggerColor="danger"
                triggerText={<TrashIcon className="button-icon" />}
                title="Creditnota verwijderen?"
                submitText="Verwijder creditnota"
                description={
                  <div>
                    Ben je zeker dat je dit credit nota wilt verwijderen? Dit kan ervoor zorgen dat de credit nota
                    nummering niet meer opeenlopend zijn.
                  </div>
                }
                onSubmit={async () => {
                  try {
                    await deleteCreditNote(creditNote.id);
                    trackEvent('creditnote_deleted', { credit_note_id: creditNote.id });
                    toast.success('Creditnota verwijderd');
                    navigate('..');
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon creditnota niet verwijderen: ${getDisplayError(err)}`);
                  }
                }}
              />
            )}

            {isAdmin && !isDraft && (
              <Dialog.Root
                open={deleteDialogOpen}
                onOpenChange={(open) => {
                  setDeleteDialogOpen(open);
                  if (!open) {
                    setDeleteStep(1);
                    setDeleteError('');
                  }
                }}
              >
                <Button
                  color="danger"
                  onTrigger={(evt) => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    setDeleteDialogOpen(true);
                  }}
                >
                  <TrashIcon className="button-icon" />
                </Button>

                <Dialog.Portal>
                  <Dialog.Overlay className="dialog-overlay" />
                  <Dialog.Content className="dialog-content">
                    {deleteStep === 1 && (
                      <>
                        <Dialog.Title className="heading-one mb-4">Creditnota verwijderen?</Dialog.Title>
                        <Dialog.Description asChild>
                          <div className="mb-4">
                            <p className="font-semibold mb-2">
                              Let op: je staat op het punt een goedgekeurde creditnota te verwijderen (
                              {creditNote.creditNoteNumber}).
                            </p>
                            <p>
                              Dit kan ervoor zorgen dat de credit nota nummering niet meer opeenlopend zijn. Deze actie
                              is onomkeerbaar en wordt gelogd.
                            </p>
                          </div>
                        </Dialog.Description>
                        <div className="flex justify-between">
                          <BaseButton onClick={() => setDeleteDialogOpen(false)}>Annuleer</BaseButton>
                          <BaseButton color="primary" onClick={() => setDeleteStep(2)}>
                            Ja, ik wil deze creditnota verwijderen
                          </BaseButton>
                        </div>
                      </>
                    )}
                    {deleteStep === 2 && (
                      <>
                        <Dialog.Title className="heading-one mb-4">Laatste bevestiging</Dialog.Title>
                        <Dialog.Description asChild>
                          <div className="mb-4">
                            <p className="font-semibold mb-2">
                              Weet je het zeker? Creditnota {creditNote.creditNoteNumber} wordt definitief verwijderd.
                            </p>
                            <p>Deze actie wordt gelogd en is gelimiteerd tot 3 keer per uur.</p>
                          </div>
                        </Dialog.Description>
                        {deleteError && <div className="text-sm mb-2 w-full">{deleteError}</div>}
                        <div className="flex justify-between">
                          <BaseButton disabled={deleteLoading} onClick={() => setDeleteDialogOpen(false)}>
                            Annuleer
                          </BaseButton>
                          <BaseButton
                            color="primary"
                            isLoading={deleteLoading}
                            disabled={deleteLoading}
                            onClick={async (event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setDeleteLoading(true);
                              try {
                                await deleteCreditNote(creditNote.id, { confirm: true });
                                trackEvent('creditnote_deleted', {
                                  credit_note_id: creditNote.id,
                                  was_draft: false,
                                });
                                toast.success('Creditnota verwijderd');
                                navigate('..');
                              } catch (err) {
                                captureException(err);
                                setDeleteError(getDisplayError(err));
                              }
                              setDeleteLoading(false);
                            }}
                          >
                            Definitief verwijderen
                          </BaseButton>
                        </div>
                      </>
                    )}
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog.Root>
            )}
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-7">
        <SummaryBar>
          <DataField title="Klant">
            {creditNote.customer.name} ({creditNote.customer.id})
          </DataField>
          <DataField title="Gestructureerde mededeling">{creditNote.structuredComment}</DataField>
          <DataField title="Factuur datum">{formatDate(creditNote.date)}</DataField>
          {creditNote.sentAt && <DataField title="Verstuurd op">{formatDate(creditNote.sentAt)}</DataField>}
          {creditNote.sentAt && <DataField title="Vervalt op">{formatDate(creditNote.expiresAt)}</DataField>}
          {creditNote.paidAt && <DataField title="Betaald op">{formatDateTime(creditNote.paidAt)}</DataField>}

          {!!creditNote.internalNotes && (
            <DataField className="w-full" title="Interne Notities">
              {creditNote.internalNotes}
            </DataField>
          )}
          {!!creditNote.externalNotes && (
            <DataField className="w-full" title="Externe Notities">
              {creditNote.externalNotes}
            </DataField>
          )}
        </SummaryBar>

        {creditNote.status !== CreditNoteStatus.Draft && (
          <PeppolStatusCard
            peppol={creditNote.peppol}
            blockReason={creditNote.peppolAvailability}
            onSend={() => sendCreditNoteViaPeppol(creditNote.id)}
            onRefresh={() => refreshCreditNotePeppolStatus(creditNote.id)}
            onChanged={refreshData}
          />
        )}

        <>
          {creditNote.creditNoteOrders.map((creditNoteOrder) => {
            const orderTotals = getInvoiceTotals(
              creditNoteOrder.lines
                .map((l) => {
                  return {
                    ...l,
                    vatRateId: l.vatRate.id,
                  };
                })
                .flat(),
            );
            const order = creditNoteOrder.order;
            const stops = order.lines.map((l) => l.stops).flat();

            return (
              <div className="card" key={`order-${order.orderNumber}`}>
                <div className="card-heading">
                  <Link to={`/internal/orders/${order.id}/general`} className="link-text">
                    Order {order.orderNumber} - {order.customerRef}
                  </Link>
                </div>

                <div className="card-content flex flex-col gap-3.5">
                  <div className="flex flex-col gap-2">
                    {stops
                      .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                      .map((stop) => {
                        return (
                          <div className="grid grid-cols-4 items-center gap-3 text-sm" key={`stop-${stop.id}`}>
                            <div className="font-semibold text-fg">
                              {stop.type === OrderLineStopType.Load ? 'Laden' : 'Lossen'}
                            </div>
                            <div className="text-fg-secondary">
                              {stop.location.country}-{stop.location.name}
                            </div>

                            <div className="flex items-center gap-1.5 text-fg-secondary">
                              <CalendarDotsIcon className="h-3.5 w-3.5 text-fg-muted" />
                              {formatDate(stop.date)}
                            </div>
                            <div className="flex items-center gap-1.5 text-fg-secondary">
                              <ClockIcon className="h-3.5 w-3.5 text-fg-muted" />
                              {`${formatInputTime(stop.timeStart)}-${formatInputTime(stop.timeEnd)}`}
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  <div className="overflow-hidden rounded-md border border-border">
                    <div className="grid grid-cols-4 gap-x-4 border-b border-border bg-surface-sunken px-3.5 py-2">
                      <div className="label-text">Beschrijving</div>
                      <div className="label-text">Aantal</div>
                      <div className="label-text">Eenheidsprijs</div>
                      <div className="label-text">Totaal</div>
                    </div>

                    <div className="grid grid-cols-4 gap-x-4 gap-y-1 px-3.5 py-2.5 text-sm">
                      {creditNoteOrder.lines.map((line) => {
                        return (
                          <React.Fragment key={`line-${line.id}`}>
                            <div>{line.productType.nameNl}</div>
                            <div>
                              {decimateAndFormatNumber(line.amount, 2, {
                                decimalSeperator: ',',
                              })}
                            </div>
                            <div>
                              €{' '}
                              {decimateAndFormatNumber(line.unitPrice, 2, {
                                decimalSeperator: ',',
                              })}
                            </div>
                            <div>
                              €{' '}
                              {decimateAndFormatNumber((line.amount * line.unitPrice) / 100, 2, {
                                decimalSeperator: ',',
                              })}
                            </div>

                            {!!line.notes && (
                              <div className="col-span-5">
                                <div className="text-sm">Extra informatie: {line.notes}</div>
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  <div className="whitespace-nowrap text-xs text-fg-muted">
                    Order totaal: €{' '}
                    {decimateAndFormatNumber(orderTotals.totalExclVat, 2, {
                      decimalSeperator: ',',
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </>

        {creditNote.manualLines.length > 0 && (
          <Card title="Manuele lijnen" flush>
            <div className="grid grid-cols-4 gap-x-4 border-b border-border px-5 py-2">
              <div className="label-text">Beschrijving</div>
              <div className="label-text">Aantal</div>
              <div className="label-text">Eenheidsprijs</div>
              <div className="label-text">Totaal</div>
            </div>

            {creditNote.manualLines.map((manualLine) => {
              return (
                <div key={`manual-line-${manualLine.id}`}>
                  <div className="grid grid-cols-4 gap-x-4 gap-y-1 px-5 py-2 text-sm">
                    <div>{manualLine.description}</div>
                    <div>
                      {decimateAndFormatNumber(manualLine.amount, 2, {
                        decimalSeperator: ',',
                      })}
                    </div>
                    <div>
                      €{' '}
                      {decimateAndFormatNumber(manualLine.unitPrice, 2, {
                        decimalSeperator: ',',
                      })}
                    </div>
                    <div>
                      €{' '}
                      {decimateAndFormatNumber((manualLine.amount * manualLine.unitPrice) / 100, 2, {
                        decimalSeperator: ',',
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </Card>
        )}

        <TotalsCard
          rows={[
            {
              label: 'Totaal excl. btw:',
              value: `€ ${decimateAndFormatNumber(totals.totalExclVat, 2, { decimalSeperator: ',' })}`,
            },
            {
              label: 'Totaal btw:',
              value: `€ ${decimateAndFormatNumber(totals.totalVat, 2, { decimalSeperator: ',' })}`,
            },
            {
              label: 'Totaal incl. btw:',
              isEmphasised: true,
              value: `€ ${decimateAndFormatNumber(totals.totalInclVat, 2, { decimalSeperator: ',' })}`,
            },
          ]}
        />

        <div>
          <h2 className="heading-two mb-2">Historiek</h2>
          <AuditLogsList key={creditNote.id} filter={{ creditNoteId: creditNote.id }} refreshToken={historyVersion} />
        </div>
      </div>
    </>
  );
};

export default CreditNotePage;
